// final

import React, { useEffect, useState } from "react";
import { Route, Routes, Navigate, useNavigate } from "react-router-dom";
import { useContext } from "react";
import { AuthContext } from "./context/AuthContext";
import LoginPage from "./components/LoginPage";
import UnityRoom from "./components/TherapyRooms/UnityRoom";
import TestPage from "./components/TherapyRooms/TestPage";
import AdminPage from "./pages/admin/AdminPage";
import TherapistAdminPage from "./pages/therapistAdmin/TherapistAdminPage";
import allowedUserIds from "./pages/admin/adminConfig";
import ClientTherapyRooms from "./components/TherapyRooms/ClientTherapyRooms";
import TherapistTherapyRooms from "./components/TherapyRooms/TherapistTherapyRooms";
import CreateMeetingDashboard from "./pages/admin/CreateMeetingDashboard";
import AdminRegisterUser from "./pages/admin/AdminRegisterUser";
import TherapistCreateMeetingDashboard from "./pages/therapistAdmin/TherapistCreateMeetingDashboard";

import TherapistAdminResendPasswordDashboard from "./pages/therapistAdmin/TherapistAdminResendPasswordDashboard";
import TherapistPresignLinkGenerator from "./pages/therapistAdmin/PresignLinkGenerator/TherapistPresignLinkGenerator";
import AdminResendPasswordDashboard from "./pages/admin/AdminResendPasswordDashboard";
import DemoPage from "./components/Demo/DemoPage";
import PresignLinkGenerator from "./pages/admin/PresignLinkGenerator/PresignLinkGenerator";
import RegisterCompanion from "./components/RegisterationForms/RegisterCompanion";
import RegisterTherapy from "./components/RegisterationForms/RegisterTherapy";
import TermsPage from "./pages/TermsPage";
import PrivacyPage from "./pages/PrivacyPage";
import Env from "./runtimeEnv";
import SessionFeedbackModal from "./components/SessionFeedbackModal";

import axios from "axios";

function App() {
  const navigate = useNavigate();

  const {
    currentNickname,
    setCurrentNickname,
    PlutonicuserID,
    setPlutonicuserID,
    isAuthenticated,
    setIsAuthenticated,
    isAdmin,
    setIsAdmin,
    userType,
    setUserType,
    loading,
    setLoading,
    currentFormType,
    setCurrentFormType,
    handleLogout,
  } = useContext(AuthContext);

  const [accessToken, setAccessToken] = useState("");

  useEffect(() => {
    const storedUserType = localStorage.getItem("userType");
    if (storedUserType) {
      setUserType(storedUserType);
    }
  }, []);

  const idleTimeout = 60 * 60 * 1000; // Idle timeout duration (10 minutes)
  let idleTimer = null;

  // Synchronize state with localStorage on app load
  useEffect(() => {
    const restoreSession = async () => {
      const savedNickname = localStorage.getItem("currentNickname");
      const savedPlutonicuserID = localStorage.getItem("PlutonicuserID");
      const lastActiveTime = localStorage.getItem("lastActiveTime");
      const sessionActive = sessionStorage.getItem("sessionActive");

      if (lastActiveTime) {
        const now = new Date();
        const lastActiveDate = new Date(lastActiveTime);
        const differenceInMinutes = Math.floor(
          (now - lastActiveDate) / (1000 * 60)
        );

        // If more than 5 minutes have passed since the last activity
        if (differenceInMinutes >= 60) {
          // console.log('[App] User session expired due to tab inactivity.');
          handleLogout();
          return;
        }
      }

      if (!sessionActive) {
        // console.log('[App] Initializing session...');
        sessionStorage.setItem("sessionActive", "true");
        setIsAuthenticated(false);
      } else if (savedNickname && savedPlutonicuserID) {
        // console.log('[App] Restoring user state from localStorage...');
        setCurrentNickname(savedNickname);
        setPlutonicuserID(savedPlutonicuserID);
        setIsAuthenticated(true);
        setIsAdmin(allowedUserIds.includes(savedPlutonicuserID));
        const storedUserType = localStorage.getItem("userType");
        if (storedUserType) setUserType(storedUserType);

        // Restore the last visited page
        const lastVisitedPage = localStorage.getItem("lastVisitedPage") || "/";
        if (
  lastVisitedPage &&
  window.location.pathname === "/" &&
  !window.history.state?.usr?.tab
) {
  navigate(lastVisitedPage, { replace: true });
}
      }

      setLoading(false); // End loading once the session is restored
    };

    restoreSession();
  }, [navigate]);

  // Idle timeout handler
  const resetIdleTimer = () => {
    if (idleTimer) clearTimeout(idleTimer);
    idleTimer = setTimeout(() => {
      // console.log('[App] User logged out due to inactivity.');
      handleLogout(); // Logout user if idle
    }, idleTimeout);
  };

  // Handle tab close to set online status to false
  useEffect(() => {
    const handleTabClose = () => {
      const lastActiveTime = new Date().toISOString();
      localStorage.setItem("lastActiveTime", lastActiveTime);

      const meetingID = sessionStorage.getItem("meetingID");
      const PlutonicuserID = localStorage.getItem("PlutonicuserID");

      if (meetingID && PlutonicuserID) {
        const payload = { meetingID, online: false };
        // console.log('[App] Sending tab close payload:', payload);

        navigator.sendBeacon(
          `${Env.REACT_APP_PYTHON_API_URL}/api/set-online-status/${PlutonicuserID}`,
          JSON.stringify(payload)
        );
      }
    };

    window.addEventListener("beforeunload", handleTabClose);

    return () => {
      window.removeEventListener("beforeunload", handleTabClose);
    };
  }, []);

  useEffect(() => {
    if (isAuthenticated) {
      const events = ["mousemove", "keypress", "click"];
      const resetTimer = () => resetIdleTimer();

      events.forEach((event) => window.addEventListener(event, resetTimer));

      // Initialize the idle timer
      resetIdleTimer();

      return () => {
        // Cleanup event listeners and timer when component unmounts
        events.forEach((event) =>
          window.removeEventListener(event, resetTimer)
        );
        if (idleTimer) clearTimeout(idleTimer);
      };
    }
  }, [isAuthenticated]);

  // Update nickname function
  const updateNickname = (nickname) => {
    setCurrentNickname(nickname);
    if (nickname) {
      localStorage.setItem("currentNickname", nickname);
      setIsAuthenticated(true);
    } else {
      localStorage.removeItem("currentNickname");
      setIsAuthenticated(false);
    }
  };

  if (loading) {
    // Show a loading spinner or message while session restoration is in progress
    return (
      <div
        style={{
          height: "100vh",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <p>Loading...</p>
      </div>
    );
  }

  return (
    <div className="App">
      <Routes>
        <Route
          path="/"
          element={
            loading ? (
              <p className="text-center">Loading...</p>
            ) : isAuthenticated ? (
              (() => {
                switch (userType?.toLowerCase()) {
                  case "therapist":
                    return <TherapistTherapyRooms />;
                  case "client":
                  case "client-ai-companion":
                  case "client-ai-therapy":
                  default:
                    return <ClientTherapyRooms />;
                }
              })()
            ) : (
              <LoginPage />
            )
          }
        />
        <Route
          path="/clients"
          element={
            isAuthenticated ? (
              userType?.toLowerCase() === "therapist" ? (
                <TherapistTherapyRooms />
              ) : (
                <Navigate to="/" replace />
              )
            ) : (
              <Navigate to="/login" replace />
            )
          }
        />

        

        <Route
          path="/clients/:sessionID/overview"
          element={
            isAuthenticated ? (
              userType?.toLowerCase() === "therapist" ? (
                <TherapistTherapyRooms />
              ) : (
                <Navigate to="/" replace />
              )
            ) : (
              <Navigate to="/login" replace />
            )
          }
        />

        <Route
          path="/unity-room/:roomType"
          element={
            isAuthenticated ? <UnityRoom /> : <Navigate to="/" replace />
          }
        />

        <Route path="/testpage" element={<TestPage />} />
        <Route path="/demo-beach" element={<DemoPage />} />
        {/* <Route path="/npcgpttherapist888" element={
  <AIBotPage
    setCurrentNickname={setCurrentNickname}
    currentNickname={currentNickname}
    handleLogout={handleLogout}
    setFormType={setCurrentFormType}
    PlutonicuserID={PlutonicuserID}
  />
} />  */}
        <Route
          path="/admin"
          element={
            isAdmin ? (
              <AdminPage
                currentNickname={currentNickname}
                setCurrentNickname={updateNickname}
                handleLogout={handleLogout}
                setFormType={setCurrentFormType}
                PlutonicuserID={PlutonicuserID}
              />
            ) : (
              <Navigate to="/" replace />
            )
          }
        />

        <Route
          path="/admin/schedule"
          element={isAdmin ? <CreateMeetingDashboard /> : <Navigate to="/" replace />}
        />
        <Route path="/admin/register-user" element={isAdmin ? <AdminRegisterUser /> : <Navigate to="/" replace />} />
        <Route
          path="/admin/createmeeting"
          element={<CreateMeetingDashboard />}
        />

        <Route
          path="/admin/updatemeeting"
          element={
            isAdmin ? <CreateMeetingDashboard /> : <Navigate to="/" replace />
          }
        />
        <Route
          path="/admin/resendpassword"
          element={
            isAdmin ? (
              <AdminResendPasswordDashboard />
            ) : (
              <Navigate to="/" replace />
            )
          }
        />

        <Route
          path="/admin/presignlinkgenerator"
          element={
            isAdmin ? <PresignLinkGenerator /> : <Navigate to="/" replace />
          }
        />


        <Route
        path="/clients/schedule"
        element={
          isAuthenticated && userType?.toLowerCase() === "therapist" ? (
            <TherapistAdminPage
              currentNickname={currentNickname}
              setCurrentNickname={updateNickname}
              handleLogout={handleLogout}
              setFormType={setCurrentFormType}
              PlutonicuserID={PlutonicuserID}
            />
          ) : (
            <Navigate to="/" replace />
          )
        }
        />

        <Route
  path="/clients/schedule/session"
  element={isAuthenticated && userType?.toLowerCase() === "therapist" ? <TherapistCreateMeetingDashboard /> : <Navigate to="/" replace />}
  />
  <Route
  path="/clients/schedule/create"
  element={
    isAuthenticated && userType?.toLowerCase() === "therapist" ? (
      <TherapistCreateMeetingDashboard />
    ) : (
      <Navigate to="/" replace />
    )
  }
/>

        <Route
          path="/clients/schedule/update"
          element={
            isAuthenticated && userType?.toLowerCase() === "therapist"
              ? <TherapistCreateMeetingDashboard />
              : <Navigate to="/" replace />
          }
        />

        {/* <Route
          path="/therapistAdmin/resendpassword"
          element={
            isAdmin ? (
              <TherapistAdminResendPasswordDashboard />
            ) : (
              <Navigate to="/" replace />
            )
          }
        /> */}

        <Route
          path="/clients/schedule/link-generator"
          element={
            isAuthenticated && userType?.toLowerCase() === "therapist"
              ? <TherapistPresignLinkGenerator />
              : <Navigate to="/" replace />
          }
        />

        <Route path="/register/ai-companion" element={<RegisterCompanion />} />
        <Route
          path="/register/ai-enabled-therapy"
          element={<RegisterTherapy />}
        />

        <Route path="/terms" element={<TermsPage />} />
        <Route path="/privacy" element={<PrivacyPage />} />

        {process.env.NODE_ENV === "development" && (
          <Route
            path="/feedback-preview"
            element={
              <div className="min-h-screen bg-[#D8F8FF]">
                <SessionFeedbackModal isOpen onClose={() => {}} />
              </div>
            }
          />
        )}

        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </div>
  );
}

export default App;
